import { Link, useSearchParams } from 'react-router-dom'
import type { Teoripost } from '../types'
import {
  allaTeoriposter,
  hittaTeoripost,
  kallorFor,
  KALLKRITIK_SAKNAS,
  KATEGORI_ETIKETT,
  TYP_ETIKETT,
} from '../lib/teorisektion'
import { useSidTitel } from '../lib/seo'
import { KallRad } from './ModellSida'

function Valjare({
  etikett,
  varde,
  onValj,
}: {
  etikett: string
  varde: string
  onValj: (id: string) => void
}) {
  return (
    <label className="jamfor-valjare">
      {etikett}
      <select value={varde} onChange={(e) => onValj(e.target.value)}>
        <option value="">Välj modell eller teori …</option>
        {allaTeoriposter.map((p) => (
          <option key={p.teori_id} value={p.teori_id}>
            {p.namn}
          </option>
        ))}
      </select>
    </label>
  )
}

/** En kolumn i jämförelsen — fälten i samma ordning som i grannkolumnen, så
 *  att kärnpoäng står mot kärnpoäng, figur mot figur osv. */
function JamforKolumn({ post }: { post: Teoripost }) {
  const kallor = kallorFor(post)
  return (
    <article className="jamfor-kolumn" aria-label={post.namn}>
      <div className="teorikort-meta">
        <span className={`typ-badge typ-${post.typ}`}>{TYP_ETIKETT[post.typ]}</span>
        <span className="teorikort-kategori">{KATEGORI_ETIKETT[post.kategori]}</span>
      </div>
      <h2>
        <Link to={`/modeller/${post.teori_id}`}>{post.namn}</Link>
      </h2>
      {post.upphov && <p className="teoripost-upphov">{post.upphov}</p>}

      <h3>Kärnpoäng</h3>
      <p className="teoripost-karnpoang">{post.karnpoang}</p>

      <h3>Figur</h3>
      {post.illustration ? (
        <span className="illustration">
          <img src={post.illustration.fil} alt={post.illustration.alt} />
        </span>
      ) : (
        <p className="jamfor-tomtext">Ingen figur i läromedlet.</p>
      )}

      <h3>Förklaring</h3>
      <p>{post.forklaring}</p>

      <h3>Källkritik</h3>
      {/* Tomt fält är INTE ett friskintyg — standardtexten i stället för tom
          yta bredvid grannmodellens förbehåll (styrdokumentets konvention). */}
      <p className={post.kallkritik ? undefined : 'jamfor-tomtext'}>
        {post.kallkritik ?? KALLKRITIK_SAKNAS}
      </p>

      <h3>Läs mer</h3>
      <ul>
        {post.kapitel.map((ref) => (
          <li key={`${ref.slug}-${ref.avsnitt_rubrik}`}>
            <Link to={`/kapitel/${ref.slug}${ref.ankare ? `#${ref.ankare}` : ''}`}>
              Kapitel {ref.kapitel_id}: {ref.titel}
            </Link>
          </li>
        ))}
      </ul>

      {kallor.length > 0 && (
        <>
          <h3>Källa{kallor.length > 1 ? 'r' : ''}</h3>
          <ul className="teoripost-kallor">
            {kallor.map((kalla) => (
              <li key={kalla.kalla_id}>
                <KallRad kalla={kalla} />
              </li>
            ))}
          </ul>
        </>
      )}
    </article>
  )
}

/** Jämförelseverktyget: två valfria poster sida vid sida, fält mot fält.
 *  Valet ligger i adressen (?a=&b=) så att en jämförelse kan länkas och delas. */
export default function ModellJamforelse() {
  useSidTitel('Jämför modeller och teorier')
  const [params, setParams] = useSearchParams()
  const a = hittaTeoripost(params.get('a') ?? '')
  const b = hittaTeoripost(params.get('b') ?? '')

  const satt = (nyckel: 'a' | 'b') => (id: string) => {
    const nya = new URLSearchParams(params)
    if (id) nya.set(nyckel, id)
    else nya.delete(nyckel)
    setParams(nya, { replace: true })
  }

  // Förslag ur jamforbar_med för den valda sidan — men vilka två som helst får jämföras.
  const forslag = a && !b ? a.jamforbar_med.map((id) => hittaTeoripost(id)).filter((p) => p !== null) : []

  return (
    <div className="wrap">
      <section className="hero">
        <nav className="brodsmula no-print" aria-label="Brödsmulor">
          <Link to="/modeller">Modeller och teorier</Link>
        </nav>
        <h1>Jämför två modeller</h1>
        <p>
          Ställ två modeller eller teorier bredvid varandra — kärnpoäng mot kärnpoäng, figur mot
          figur — och se vad var och en fångar som den andra missar.
        </p>
      </section>

      <div className="jamfor-valrad no-print">
        <Valjare etikett="Första modellen" varde={a?.teori_id ?? ''} onValj={satt('a')} />
        <Valjare etikett="Andra modellen" varde={b?.teori_id ?? ''} onValj={satt('b')} />
      </div>

      {forslag.length > 0 && (
        <p className="jamfor-forslag no-print">
          Jämförs ofta med:{' '}
          {forslag.map((p) => (
            <button key={p.teori_id} type="button" className="knapp knapp-sekundar knapp-liten" onClick={() => satt('b')(p.teori_id)}>
              {p.namn}
            </button>
          ))}
        </p>
      )}

      {a && b ? (
        <div className="jamfor-kolumner">
          <JamforKolumn post={a} />
          <JamforKolumn post={b} />
        </div>
      ) : (
        <p className="sektionsingress">Välj två modeller ovan för att jämföra dem.</p>
      )}
    </div>
  )
}
